import type {
  OptionDefinition,
  Product,
  ProductColorOption,
  ProductVariant,
  UnavailableReason,
  VariantSelections,
} from "@/interfaces/product";

const DEFAULT_MIN_IN_STOCK = 1;

const clean = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const optionKey = (option: { name?: string; position?: number | null }) =>
  String(option.position ?? option.name ?? "");

export const getVariantOptionKey = optionKey;

export function getVariantOptionValues(variant?: ProductVariant | null): string[] {
  if (!variant) return [];
  if (Array.isArray(variant.options)) return variant.options.map(clean);
  return [variant.option1, variant.option2, variant.option3].map(clean).filter(Boolean);
}

export function getProductVariantOptions(product?: Product | null) {
  return product?.variantOptions?.options ?? [];
}

export function getProductVariantCombinations(product?: Product | null) {
  return product?.variantOptions?.combinations ?? [];
}

export function productForVariant(product: Product, variantId: string): Product {
  const variant = getProductVariantCombinations(product).find((v) => v.variantId === variantId);
  if (!variant) return product;
  return {
    ...product,
    imageUrl: variant.imageUrl || product.imageUrl,
    variantTitle: variant.variantTitle ?? product.variantTitle,
  };
}

export function isVariantAvailable(variant?: ProductVariant | null) {
  if (!variant || variant.enabled === false || variant.available === false || variant.outOfStock)
    return false;
  if (variant.inventoryManagement) {
    return (variant.qty ?? 0) >= (variant.minInStock ?? DEFAULT_MIN_IN_STOCK);
  }
  return true;
}

function inferPositions(product: Product, definitions: Omit<OptionDefinition, "position">[]) {
  const variants = getProductVariantCombinations(product);
  const maxOptions = Math.max(
    0,
    ...variants.map((variant) => getVariantOptionValues(variant).length),
  );
  const used = new Set<number>();

  return definitions.map((definition, index) => {
    const suppliedPosition = Number(definition.key);
    if (Number.isInteger(suppliedPosition) && suppliedPosition > 0) {
      used.add(suppliedPosition);
      return { ...definition, position: suppliedPosition };
    }

    const values = new Set(definition.values.map(clean).filter(Boolean));
    let inferred = 0;
    for (let position = 1; position <= maxOptions; position += 1) {
      if (used.has(position)) continue;
      const valuesAtPosition = new Set(
        variants.map((variant) => getVariantOptionValues(variant)[position - 1]).filter(Boolean),
      );
      if ([...values].some((value) => valuesAtPosition.has(value))) {
        inferred = position;
        break;
      }
    }

    const fallback = inferred || index + 1;
    used.add(fallback);
    return { ...definition, position: fallback };
  });
}

export function getProductOptionDefinitions(product?: Product | null): OptionDefinition[] {
  if (!product) return [];
  const rawDefinitions: Omit<OptionDefinition, "position">[] = [];
  if (product.colors?.length) {
    const firstColor = product.colors[0];
    rawDefinitions.push({
      key: String(firstColor?.position ?? firstColor?.optionName ?? "Color"),
      name: firstColor?.optionName ?? "Color",
      values: product.colors.map((color) => color.label).filter(Boolean),
      isColor: true,
    });
  }

  for (const option of getProductVariantOptions(product)) {
    rawDefinitions.push({
      key: optionKey(option),
      name: option.name,
      values: option.values.map((value) => value.title).filter(Boolean),
    });
  }

  return inferPositions(product, rawDefinitions);
}

function selectionByPosition(
  product: Product,
  selectedColor: string | null,
  selections: VariantSelections,
) {
  const byPosition: Record<number, string> = {};
  for (const definition of getProductOptionDefinitions(product)) {
    if (definition.isColor) {
      const color = product.colors?.find(
        (entry) => entry.value === selectedColor || entry.label === selectedColor,
      );
      byPosition[definition.position] = color?.label ?? clean(selectedColor);
    } else {
      byPosition[definition.position] = clean(
        selections[definition.key] ?? selections[definition.name],
      );
    }
  }
  return byPosition;
}

function matchesSelections(variant: ProductVariant, selectedByPosition: Record<number, string>) {
  const options = getVariantOptionValues(variant);
  return Object.entries(selectedByPosition).every(([position, selected]) =>
    selected ? options[Number(position) - 1] === selected : true,
  );
}

export function resolveProductVariant(
  product: Product | null,
  selectedColor: string | null,
  selections: VariantSelections,
): {
  activeVariant: ProductVariant | null;
  exactVariant: ProductVariant | null;
  isUnavailable: boolean;
  unavailableReason: UnavailableReason | null;
} {
  const variants = getProductVariantCombinations(product);
  if (!product || !variants.length) {
    return {
      activeVariant: null,
      exactVariant: null,
      isUnavailable: false,
      unavailableReason: null,
    };
  }

  const selectedByPosition = selectionByPosition(product, selectedColor, selections);
  if (Object.values(selectedByPosition).some((value) => !value)) {
    return {
      activeVariant: null,
      exactVariant: null,
      isUnavailable: true,
      unavailableReason: "missing-selection" as const,
    };
  }

  const exactVariant =
    variants.find((variant) => matchesSelections(variant, selectedByPosition)) ?? null;
  if (!exactVariant) {
    return {
      activeVariant: null,
      exactVariant: null,
      isUnavailable: true,
      unavailableReason: "invalid-combination" as const,
    };
  }

  if (!isVariantAvailable(exactVariant)) {
    return {
      activeVariant: null,
      exactVariant,
      isUnavailable: true,
      unavailableReason: exactVariant.outOfStock
        ? ("out-of-stock" as const)
        : ("unavailable-variant" as const),
    };
  }

  return {
    activeVariant: exactVariant,
    exactVariant,
    isUnavailable: false,
    unavailableReason: null,
  };
}

export function getSelectableValues(
  product: Product | null,
  selectedColor: string | null,
  selections: VariantSelections,
) {
  const variants = getProductVariantCombinations(product);
  if (!product || !variants.length) return new Map<string, Set<string>>();
  const definitions = getProductOptionDefinitions(product);
  const selectedByPosition = selectionByPosition(product, selectedColor, selections);
  const result = new Map<string, Set<string>>();

  for (const definition of definitions) {
    const values = new Set<string>();
    for (const variant of variants) {
      if (!isVariantAvailable(variant)) continue;
      const options = getVariantOptionValues(variant);
      const matchesOtherSelections = Object.entries(selectedByPosition).every(
        ([position, selected]) => {
          const numericPosition = Number(position);
          return (
            numericPosition === definition.position ||
            !selected ||
            options[numericPosition - 1] === selected
          );
        },
      );
      const optionValue = options[definition.position - 1];
      if (matchesOtherSelections && optionValue) values.add(optionValue);
    }
    result.set(definition.key, values);
  }

  return result;
}

export function pickDefaultVariant(product?: Product | null) {
  const variants = getProductVariantCombinations(product);
  return variants.find(isVariantAvailable) ?? variants[0] ?? null;
}

export function selectionsFromVariant(product: Product | null, variant: ProductVariant | null) {
  const selections: VariantSelections = {};
  let selectedColor: string | null = null;
  if (!product || !variant) return { selectedColor, selections };
  const options = getVariantOptionValues(variant);
  for (const definition of getProductOptionDefinitions(product)) {
    const value = options[definition.position - 1] ?? "";
    if (definition.isColor) {
      selectedColor =
        product.colors?.find((color: ProductColorOption) => color.label === value)?.value ?? null;
    } else {
      selections[definition.key] = value;
    }
  }
  return { selectedColor, selections };
}

export function getProductAvailabilityStatus(product: Product | null) {
  if (!product) return { shouldAutoSelect: false, isEntirelyOutOfStock: false };
  const variants = getProductVariantCombinations(product);
  if (!variants.length) return { shouldAutoSelect: false, isEntirelyOutOfStock: false };

  const isEntirelyOutOfStock = variants.every((v) => !isVariantAvailable(v));
  // Every product opens on a resolved variant, so the CTA is live without a click. This used to
  // fire only when the primary variant was out of stock, which left the common case — a product
  // whose first variant IS in stock — with nothing selected and the CTA stuck behind
  // "Select your options to continue". The one case that must NOT auto-select is a product with
  // no in-stock variant at all: picking one there would be a false default on a gift nobody can
  // redeem, so the swatches stay grey and the CTA stays disabled.
  const shouldAutoSelect = !isEntirelyOutOfStock;

  return { shouldAutoSelect, isEntirelyOutOfStock };
}

/**
 * Whether an option is a real choice. Shopify ships single-value options ("Color: Midnight" on a
 * product that only comes in Midnight), and rendering one gives the shopper a label plus a lone
 * control that decides nothing — and, when its swatch has no resolvable colour, an empty box that
 * blocks checkout outright. Hidden from the UI only: resolution still feeds the value through, so
 * the variant resolves exactly as if the shopper had picked the single option themselves.
 */
export function isChoosableOption(valueCount: number) {
  return valueCount > 1;
}

/** Colour swatches worth rendering: none for a single-colour product. */
export function showsColorPicker(product?: Product | null) {
  return isChoosableOption(product?.colors?.length ?? 0);
}

/** The dropdowns worth rendering — size, scent and friends — minus every single-value option.
 *  Pair with `getProductVariantOptions`, which stays unfiltered because resolution needs them all. */
export function getVisibleVariantOptions(product?: Product | null) {
  return getProductVariantOptions(product).filter((option) =>
    isChoosableOption(option.values?.length ?? 0),
  );
}
